import React, { useState, useRef, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faMagic,
  faTimes,
  faSpinner,
  faDownload,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import socialApiClient from "../../cores/helpers/services/social-api-client";
import { useMeCore } from "../../cores/me";
import { useIntl } from "react-intl";

const GENERATE_ENDPOINT = "nanobanana/generate";
const GENERATE_OR_EDIT_ENDPOINT = "nanobanana/generate-or-edit";
const STATUS_ENDPOINT = "nanobanana/status";

interface UploadedImage {
  id: string;
  file: File;
  preview: string;
}

interface GeneratedImage {
  id: string;
  url: string;
  prompt: string;
  timestamp: number;
}

export default function AIGeneration() {
  const { companyId } = useMeCore();
  const intl = useIntl();
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [editDescription, setEditDescription] = useState("");
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<string | null>(null);
  const [canCheckStatus, setCanCheckStatus] = useState(false);
  const [aspectRatio, setAspectRatio] = useState("4:5");
  const [resolution, setResolution] = useState("1K");
  const [apiMode, setApiMode] = useState<"pro" | "edit">("edit");
  const [callBackUrl, setCallBackUrl] = useState("");
  const [numImages, setNumImages] = useState(1);
  const [imageSize, setImageSize] = useState("4:5");
  const [watermark, setWatermark] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const maxImages = 3;
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  useEffect(() => {
    const loadHistory = async () => {
      if (!companyId) return;
      try {
        const { data } = await socialApiClient().get("nanobanana/history", {
          params: { companyId, limit: 50 },
        });
        const items = data?.payload || [];
        const mapped: GeneratedImage[] = items.map((item: any) => ({
          id: String(item.id),
          url: item.imageUrl,
          prompt: item.prompt || "",
          timestamp: new Date(item.createdAt).getTime() || Date.now(),
        }));
        setGeneratedImages(mapped);
      } catch {
        // Si falla el historial, simplemente arrancamos vacío
      }
    };
    loadHistory();
  }, [companyId]);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    let rejectedBySize = 0;
    let rejectedByType = 0;
    let rejectedByLimit = 0;
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith("image/")) {
        rejectedByType += 1;
        return;
      }
      if (file.size > maxFileSize) {
        rejectedBySize += 1;
        return;
      }
      if (uploadedImages.length + validFiles.length >= maxImages) {
        rejectedByLimit += 1;
        return;
      }
      validFiles.push(file);
    });

    const newImages: UploadedImage[] = validFiles.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
    }));

    setUploadedImages((prev) => [...prev, ...newImages].slice(0, maxImages));
    if (rejectedBySize > 0) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errFileSizeExceeded" }));
      return;
    }
    if (rejectedByType > 0) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errInvalidFileType" }));
      return;
    }
    if (rejectedByLimit > 0) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errMaxImagesExceeded" }, { maxImages }));
      return;
    }
    setErrorMessage(null);
  }, [uploadedImages.length]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (e.target) e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const removeImage = (id: string) => {
    setUploadedImages((prev) => {
      const image = prev.find((img) => img.id === id);
      if (image) {
        URL.revokeObjectURL(image.preview);
      }
      return prev.filter((img) => img.id !== id);
    });
  };

  const downloadGeneratedImage = async (image: GeneratedImage) => {
    try {
      const { data } = await socialApiClient().get("nanobanana/download-image", {
        responseType: "blob",
        params: { imageUrl: image.url, companyId },
      });
      const blob: Blob = data;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const datePart = new Date(image.timestamp).toISOString().slice(0, 10);
      link.download = `${datePart}_Wembii-${image.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errDownloadFailed" }));
    }
  };

  const removeGeneratedImage = (id: string) => {
    setGeneratedImages((prev) => prev.filter((img) => img.id !== id));
    socialApiClient()
      .delete(`nanobanana/history/${id}`, {
        params: { companyId },
      })
      .catch(() => {
        // Si falla, solo se queda borrada en el front; podríamos recargar historial si hace falta
      });
  };

  const addGeneratedAsReference = async (image: GeneratedImage) => {
    try {
      if (uploadedImages.length >= maxImages) {
        setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errMaxRefsExceeded" }, { maxImages }));
        return;
      }
      const { data, headers } = await socialApiClient().get(
        "nanobanana/download-image",
        {
          responseType: "blob",
          params: { imageUrl: image.url, companyId },
        }
      );
      const blob: Blob = data;
      const contentType =
        headers["content-type"] || blob.type || "image/jpeg";
      const ext = blob.type.includes("png")
        ? "png"
        : blob.type.includes("webp")
        ? "webp"
        : "jpg";
      const file = new File([blob], `referencia-${Date.now()}.${ext}`, {
        type: contentType,
      });
      const newImage: UploadedImage = {
        id: `${Date.now()}-${Math.random()}`,
        file,
        preview: URL.createObjectURL(file),
      };
      setUploadedImages((prev) => [...prev, newImage].slice(0, maxImages));
      setErrorMessage(null);
    } catch (error: any) {
      setErrorMessage(
        error?.message || intl.formatMessage({ id: "app.aiGeneration.errRefFailed" })
      );
    }
  };

  const fetchTaskStatus = async (taskId: string) => {
    if (!companyId) {
      throw new Error(intl.formatMessage({ id: "app.aiGeneration.errNoCompanyId" }));
    }
    const { data } = await socialApiClient().get(STATUS_ENDPOINT, {
      params: { taskId, companyId },
    });
    const taskPayload = data?.payload || data;
    const resultUrl =
      taskPayload?.resultImageUrl ||
      taskPayload?.raw?.response?.resultImageUrl ||
      taskPayload?.raw?.data?.response?.resultImageUrl;

    if (taskPayload?.status === "success" && resultUrl) {
      return { imageUrl: resultUrl, taskPayload };
    }

    if (taskPayload?.raw?.errorMessage) {
      throw new Error(taskPayload.raw.errorMessage);
    }

    return { imageUrl: null, taskPayload };
  };

  const pollTaskStatus = async (taskId: string) => {
    const maxAttempts = 30;
    const delayMs = 2000;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      setTaskStatus(`Consultando estado... (${attempt}/${maxAttempts})`);
      const progress = 70 + Math.round((attempt / maxAttempts) * 30);
      setGenerationProgress(progress);

      const { imageUrl } = await fetchTaskStatus(taskId);
      if (imageUrl) {
        return imageUrl;
      }

      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    throw new Error(intl.formatMessage({ id: "app.aiGeneration.errTimeout" }));
  };

  const handleGenerate = async () => {
    if (!companyId) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errCompanyRequired" }));
      return;
    }
    if (!editDescription.trim() || uploadedImages.length === 0) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errImageAndPromptRequired" }));
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setErrorMessage(null);
    setCanCheckStatus(false);
    setCurrentTaskId(null);

    try {
      setGenerationProgress(10);
      const prompt = editDescription.trim();
      const formData = new FormData();
      formData.append("prompt", prompt);
      formData.append("ecoMode", "false");
      uploadedImages.forEach((image) => {
        formData.append("images", image.file);
      });

      if (apiMode === "pro") {
        formData.append("resolution", resolution);
        formData.append("aspectRatio", aspectRatio);
      } else {
        formData.append(
          "type",
          uploadedImages.length > 0 ? "IMAGETOIAMGE" : "TEXTTOIAMGE"
        );
        formData.append("numImages", String(numImages));
        formData.append("imageSize", imageSize);
        if (watermark.trim()) {
          formData.append("watermark", watermark.trim());
        }
      }
      if (callBackUrl.trim()) {
        formData.append("callBackUrl", callBackUrl.trim());
      }

      setGenerationProgress(50);
      const endpoint =
        apiMode === "pro" ? GENERATE_ENDPOINT : GENERATE_OR_EDIT_ENDPOINT;
      const { data } = await socialApiClient().post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { companyId },
      });

      setGenerationProgress(70);
      const providerCode = data?.payload?.raw?.code;
      const providerMessage = (data?.payload?.raw?.msg || "").toString();
      const insufficientCredits =
        providerCode === 402 ||
        providerMessage.toLowerCase().includes("insufficient");
      if (insufficientCredits) {
        throw new Error(intl.formatMessage({ id: "app.aiGeneration.errInsufficientCredits" }));
      }

      const taskId = data?.payload?.taskId || data?.data?.taskId || data?.taskId;
      if (!taskId) {
        throw new Error(intl.formatMessage({ id: "app.aiGeneration.errNoTaskId" }));
      }
      setCurrentTaskId(taskId);

      const imageUrl = await pollTaskStatus(taskId);

      setGenerationProgress(100);
      setCanCheckStatus(false);
      setCurrentTaskId(null);

      const generatedImage: GeneratedImage = {
        id: `${Date.now()}`,
        url: imageUrl,
        prompt: editDescription,
        timestamp: Date.now(),
      };

      setGeneratedImages((prev) => [generatedImage, ...prev]);
    } catch (error: any) {
      console.error("Error completo:", error);
      console.error("Stack:", error.stack);

      const responseMessage = error?.response?.data?.message;
      const parsedResponseMessage = Array.isArray(responseMessage)
        ? responseMessage.join(" ")
        : responseMessage;
      const errorMsg =
        parsedResponseMessage ||
        error?.response?.data?.error?.message ||
        error?.message ||
        intl.formatMessage({ id: "app.aiGeneration.errUnknown" });
      setErrorMessage(errorMsg);

      const isTimeout =
        typeof errorMsg === "string" &&
        errorMsg.toLowerCase().includes("tiempo de espera agotado");
      if (isTimeout && currentTaskId) {
        setCanCheckStatus(true);
      } else {
        setCanCheckStatus(false);
        setCurrentTaskId(null);
      }
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const handleCheckStatus = async () => {
    if (!currentTaskId) return;
    setIsGenerating(true);
    setErrorMessage(null);
    try {
      const imageUrl = await pollTaskStatus(currentTaskId);
      const generatedImage: GeneratedImage = {
        id: `${Date.now()}`,
        url: imageUrl,
        prompt: editDescription,
        timestamp: Date.now(),
      };
      setGeneratedImages((prev) => [generatedImage, ...prev]);
      setCanCheckStatus(false);
      setCurrentTaskId(null);
    } catch (error: any) {
      setErrorMessage(error.message || intl.formatMessage({ id: "app.aiGeneration.errCheckStatus" }));
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      handleGenerate();
    }
  };

  return (
    <Layout>
      <Content>
        <div className="ai-generation-container" style={styles.container}>
          {/* Panel Izquierdo - Controles */}
          <div className="ai-generation-left-panel" style={styles.leftPanel}>
            {/* Sección de Subida de Imágenes */}
          <div style={styles.section}>
              <h3 style={styles.sectionTitle}>{intl.formatMessage({ id: "app.aiGeneration.sectionUploadTitle" })}</h3>
              
              {uploadedImages.length === 0 ? (
                <div
                  className="ai-upload-card"
                  style={styles.uploadCard}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div style={styles.uploadIcon}>
                    <FontAwesomeIcon icon={faUpload} size="3x" />
                  </div>
                  <h4 style={styles.uploadTitle}>{intl.formatMessage({ id: "app.aiGeneration.uploadTitle" })}</h4>
                  <p style={styles.uploadText}>
                    {intl.formatMessage({ id: "app.aiGeneration.uploadText" }, { maxImages })}
                  </p>
                  <p style={styles.uploadInfo}>
                    {intl.formatMessage({ id: "app.aiGeneration.uploadInfo" })}
                  </p>
                  <button style={styles.chooseButton}>{intl.formatMessage({ id: "app.aiGeneration.chooseButton" })}</button>
                </div>
              ) : (
                <div style={styles.imagesContainer}>
                  <div style={styles.imagesGrid}>
                    {uploadedImages.map((img, index) => (
                      <div key={img.id} style={styles.imageThumbnail}>
                        <img
                          src={img.preview}
                          alt={`Referencia ${index + 1}`}
                          style={styles.thumbnailImage}
                        />
                        <div style={styles.imageBadge}>{index + 1}</div>
                        <button
                          style={styles.removeButton}
                          onClick={() => removeImage(img.id)}
                          title="Eliminar imagen"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    ))}
                    {uploadedImages.length < maxImages && (
                      <div
                        style={styles.addImagePlaceholder}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <div style={styles.plusIcon}>+</div>
                        <div style={styles.remainingText}>
                          {intl.formatMessage({ id: "app.aiGeneration.remaining" }, { count: maxImages - uploadedImages.length })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={styles.selectionInfo}>
                    {intl.formatMessage({ id: "app.aiGeneration.selectionInfo" }, { count: uploadedImages.length, total: maxImages })}
                  </div>
                </div>
              )}

              <p style={styles.infoText}>
                {intl.formatMessage({ id: "app.aiGeneration.infoText" })}
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple
                onChange={handleFileInputChange}
                style={{ display: "none" }}
              />
            </div>

            {/* Configuración NanoBanana (API selector) temporalmente oculto
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>{intl.formatMessage({ id: "app.aiGeneration.sectionConfigTitle" })}</h3>
              <div style={styles.selectRow}>
                <span className="selectLabel" style={styles.selectLabel}>{intl.formatMessage({ id: "app.aiGeneration.apiLabel" })}</span>
                <select
                  className="ai-select"
                  style={styles.selectInput}
                  value={apiMode}
                  onChange={(e) => setApiMode(e.target.value as "pro" | "edit")}
                >
                  <option value="pro">{intl.formatMessage({ id: "app.aiGeneration.apiOptionPro" })}</option>
                  <option value="edit">{intl.formatMessage({ id: "app.aiGeneration.apiOptionEdit" })}</option>
                </select>
              </div>
            </div>
            */}

            {/* Sección de Descripción */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>{intl.formatMessage({ id: "app.aiGeneration.sectionDescTitle" })}</h3>
              <textarea
                className="ai-textarea"
                style={styles.textArea}
                placeholder={intl.formatMessage({ id: "app.aiGeneration.placeholder" })}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                onKeyDown={handleKeyPress}
                rows={6}
              />
              <div style={styles.shortcutHint}>
                <span style={styles.shortcutKey}>⌘</span>
                <span style={styles.shortcutPlus}>+</span>
                <span style={styles.shortcutKey}>Enter</span>
              </div>
            </div>

            {/* Mensaje de Error */}
            {errorMessage && (
              <div style={styles.errorMessage}>
                {errorMessage}
              </div>
            )}

            {canCheckStatus && currentTaskId && !isGenerating && (
              <>
                <button
                  style={styles.secondaryButton}
                  onClick={handleCheckStatus}
                >
                  {intl.formatMessage({ id: "app.aiGeneration.checkStatusBtn" })}
                </button>
                <div style={styles.helperText}>
                  {intl.formatMessage({ id: "app.aiGeneration.checkStatusHelper" })}
                </div>
              </>
            )}

            {/* Botón de Generar */}
            <button
              style={{
                ...styles.generateButton,
                ...(isGenerating || !editDescription.trim() || uploadedImages.length === 0
                  ? styles.generateButtonDisabled
                  : {}),
              }}
              onClick={handleGenerate}
              disabled={isGenerating || !editDescription.trim() || uploadedImages.length === 0}
            >
              {isGenerating ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "8px" }} />
                  {intl.formatMessage({ id: "app.aiGeneration.generatingBtn" })}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faMagic} style={{ marginRight: "8px" }} />
                  {intl.formatMessage({ id: "app.aiGeneration.generateBtn" })}
                </>
              )}
            </button>
          </div>

          {/* Panel Derecho - Resultados */}
          <div
            className="ai-generation-right-panel"
            style={styles.rightPanel}
          >
        {generatedImages.length > 0 ? (
          <div style={styles.resultsGrid}>
            {isGenerating && (
              <div className="ai-loading-card" style={styles.loadingCard}>
                <div style={styles.loadingSpinner}>
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                </div>
                <p style={styles.loadingText}>{intl.formatMessage({ id: "app.aiGeneration.generatingBtn" })}</p>
                {generationProgress > 0 && (
                  <div style={styles.progressBar}>
                    <div
                      style={{
                        ...styles.progressFill,
                        width: `${generationProgress}%`,
                      }}
                    />
                  </div>
                )}
                <p style={styles.progressText}>{generationProgress}%</p>
              </div>
            )}

            {generatedImages.map((img) => (
              <div key={img.id} className="ai-result-card" style={styles.resultCard}>
                <img
                  src={img.url}
                  alt="Imagen generada"
                  className="ai-result-image"
                  style={styles.resultImage}
                  onError={(e) => {
                    e.currentTarget.src = `https://picsum.photos/512/512?random=${Date.now()}`;
                  }}
                />
                <div className="ai-result-prompt" style={styles.resultPrompt}>{img.prompt}</div>
                <div className="ai-result-actions" style={styles.resultActions}>
                  <button
                    className="ai-result-action-btn"
                    style={styles.resultActionButton}
                    type="button"
                    onClick={() => downloadGeneratedImage(img)}
                  >
                    <FontAwesomeIcon icon={faDownload} /> {intl.formatMessage({ id: "app.aiGeneration.downloadBtn" })}
                  </button>
                  <button
                    className="ai-result-action-btn"
                    style={styles.resultActionButton}
                    type="button"
                    onClick={() => addGeneratedAsReference(img)}
                  >
                    <FontAwesomeIcon icon={faPlus} /> {intl.formatMessage({ id: "app.aiGeneration.useAsRefBtn" })}
                  </button>
                  <button
                    className="ai-result-action-danger-btn"
                    style={styles.resultActionDangerButton}
                    type="button"
                    onClick={() => removeGeneratedImage(img.id)}
                  >
                    <FontAwesomeIcon icon={faTimes} /> {intl.formatMessage({ id: "app.aiGeneration.deleteBtn" })}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : isGenerating ? (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}>
              <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            </div>
            <p style={styles.loadingText}>{intl.formatMessage({ id: "app.aiGeneration.generatingBtn" })}</p>
            {generationProgress > 0 && (
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${generationProgress}%`,
                  }}
                />
              </div>
            )}
            <p style={styles.progressText}>{generationProgress}%</p>
          </div>
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyGrid}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={styles.emptyCell} />
              ))}
            </div>
          </div>
        )}
      </div>
        </div>
      </Content>
    </Layout>
  );
}

// Estilos inline con colores de Wembii
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: "flex",
    height: "calc(100vh - 110px)",
    gap: "20px",
    padding: "16px 20px",
    color: "#41463d",
  },
  leftPanel: {
    width: "360px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    overflowY: "auto",
    paddingRight: "12px",
  },
  rightPanel: {
    flex: 1,
    borderRadius: "30px",
    padding: "24px",
    overflowY: "auto",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    border: "3px solid #e6e6ee",
  },
  section: {
    marginBottom: "8px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 600,
    marginBottom: "16px",
    color: "#0B65C2",
  },
  uploadCard: {
    backgroundColor: "#ffffff",
    borderRadius: "24px",
    padding: "14px 16px",
    textAlign: "center",
    cursor: "pointer",
    border: "3px dashed #e6e6ee",
    transition: "all 0.3s ease",
  },
  uploadIcon: {
    color: "#0B65C2",
    marginBottom: "16px",
  },
  uploadTitle: {
    fontSize: "15px",
    fontWeight: 600,
    marginBottom: "4px",
    color: "#41463d",
  },
  uploadText: {
    fontSize: "12px",
    color: "#6c757d",
    marginBottom: "4px",
  },
  uploadInfo: {
    fontSize: "10px",
    color: "#6c757d",
    marginBottom: "8px",
  },
  chooseButton: {
    backgroundColor: "#0B65C2",
    color: "#ffffff",
    border: "none",
    borderRadius: "25px",
    padding: "12px 24px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  imagesContainer: {
    marginBottom: "12px",
  },
  imagesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "12px",
    marginBottom: "12px",
  },
  imageThumbnail: {
    position: "relative",
    aspectRatio: "1",
    borderRadius: "15px",
    overflow: "hidden",
    backgroundColor: "#f8f9fa",
    border: "1px solid #e6e6ee",
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  imageBadge: {
    position: "absolute",
    bottom: "8px",
    left: "8px",
    backgroundColor: "#0B65C2",
    color: "#ffffff",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 600,
  },
  removeButton: {
    position: "absolute",
    top: "4px",
    right: "4px",
    backgroundColor: "rgba(210, 42, 48, 0.9)",
    color: "#ffffff",
    border: "none",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "12px",
  },
  addImagePlaceholder: {
    aspectRatio: "1",
    border: "3px dashed #e6e6ee",
    borderRadius: "15px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    backgroundColor: "#f8f9fa",
    transition: "all 0.2s ease",
  },
  plusIcon: {
    fontSize: "24px",
    color: "#0B65C2",
    marginBottom: "4px",
  },
  remainingText: {
    fontSize: "12px",
    color: "#6c757d",
  },
  selectionInfo: {
    fontSize: "12px",
    color: "#6c757d",
    textAlign: "center",
    padding: "8px",
    backgroundColor: "#f8f9fa",
    borderRadius: "15px",
    border: "1px solid #e6e6ee",
  },
  infoText: {
    fontSize: "12px",
    color: "#6c757d",
    marginTop: "8px",
    lineHeight: "1.5",
  },
  textArea: {
    width: "100%",
    backgroundColor: "#ffffff",
    border: "1px solid #e6e6ee",
    borderRadius: "15px",
    padding: "16px",
    color: "#41463d",
    fontSize: "14px",
    fontFamily: "inherit",
    resize: "vertical",
    minHeight: "220px",
  },
  modelInput: {
    width: "100%",
    backgroundColor: "#ffffff",
    border: "1px solid #e6e6ee",
    borderRadius: "12px",
    padding: "12px 14px",
    color: "#41463d",
    fontSize: "13px",
    fontFamily: "inherit",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
    fontSize: "12px",
    color: "#41463d",
  },
  checkboxLabel: {
    fontSize: "12px",
    color: "#41463d",
  },
  selectRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  selectLabel: {
    fontSize: "12px",
    color: "#41463d",
  },
  selectInput: {
    flex: 1,
    backgroundColor: "#ffffff",
    border: "1px solid #e6e6ee",
    borderRadius: "10px",
    padding: "8px 10px",
    color: "#41463d",
    fontSize: "12px",
    fontFamily: "inherit",
  },
  shortcutHint: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "8px",
    justifyContent: "flex-end",
    fontSize: "11px",
    color: "#6c757d",
  },
  shortcutKey: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #e6e6ee",
    borderRadius: "4px",
    padding: "2px 6px",
    fontSize: "10px",
    color: "#41463d",
  },
  shortcutPlus: {
    color: "#6c757d",
  },
  errorMessage: {
    backgroundColor: "#fff3cd",
    border: "1px solid #ffc107",
    borderRadius: "15px",
    padding: "12px",
    color: "#856404",
    fontSize: "14px",
    marginBottom: "8px",
  },
  statusMessage: {
    backgroundColor: "#e6f0ff",
    border: "1px solid #a9c7ff",
    borderRadius: "15px",
    padding: "10px 12px",
    color: "#1f4ea3",
    fontSize: "13px",
    marginBottom: "8px",
  },
  secondaryButton: {
    width: "100%",
    backgroundColor: "#ffffff",
    color: "#0B65C2",
    border: "1px solid #0B65C2",
    borderRadius: "25px",
    padding: "12px 20px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    marginBottom: "8px",
  },
  generateButton: {
    width: "100%",
    backgroundColor: "#0B65C2",
    color: "#ffffff",
    border: "none",
    borderRadius: "25px",
    padding: "16px 24px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    marginTop: "24px",
  },
  generateButtonDisabled: {
    backgroundColor: "#c4c4c4",
    color: "#6c757d",
    cursor: "not-allowed",
    opacity: 0.6,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
  },
  loadingSpinner: {
    color: "#0B65C2",
  },
  loadingText: {
    fontSize: "18px",
    color: "#41463d",
    fontWeight: 500,
  },
  progressBar: {
    width: "300px",
    height: "4px",
    backgroundColor: "#e6e6ee",
    borderRadius: "2px",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#0B65C2",
    transition: "width 0.3s ease",
  },
  progressText: {
    fontSize: "14px",
    color: "#6c757d",
    fontWeight: 500,
  },
  helperText: {
    fontSize: "11px",
    color: "#6c757d",
    textAlign: "center",
    marginTop: "4px",
    marginBottom: "4px",
  },
  loadingCard: {
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    border: "2px dashed #0B65C2",
    padding: "16px 12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  resultsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
    width: "100%",
    alignContent: "start",
  },
  resultCard: {
    borderRadius: "20px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  resultImage: {
    width: "100%",
    height: "360px",
    objectFit: "contain",
  },
  resultPrompt: {
    padding: "12px",
    fontSize: "12px",
    minHeight: "48px",
  },
  resultActions: {
    display: "flex",
    gap: "8px",
    padding: "10px 12px 12px",
  },
  resultActionButton: {
    flex: 1,
    borderRadius: "10px",
    padding: "8px 10px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  resultActionDangerButton: {
    flex: 1,
    borderRadius: "10px",
    padding: "8px 10px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  emptyState: {
    width: "100%",
    height: "100%",
  },
  emptyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gridTemplateRows: "repeat(3, 1fr)",
    gap: "2px",
    width: "100%",
    height: "100%",
  },
  emptyCell: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #e6e6ee",
  },
};
